import React from 'react';
import { useProgress } from '../hooks/useProgress';
import { useWeddingDay } from '../state/useWeddingDay';
import Deadline from './Deadline';

const e = React.createElement;

const Dot = ({ step }) => {
  const state = step.done
    ? 'is-done'
    : step.failed
    ? 'is-failed'
    : step.current
    ? 'is-current'
    : 'is-ahead';
  return e(
    'span',
    { className: `wd-dot ${state}` },
    step.done ? '✓' : step.failed ? '×' : String(step.index + 1)
  );
};

/**
 * One rail per section, split into a segment per stop. A segment the couple
 * is beyond reads full, and the one they are on fills as it is answered, so
 * the rail moves on both counts.
 */
const Track = ({ step }) =>
  e(
    'span',
    {
      className: 'wd-track',
      title: `${step.label}: ${Math.round(step.fill * 100)}% filled`,
    },
    step.segments.map((seg, i) =>
      e('span', { className: 'wd-tseg', key: i }, [
        e('i', {
          key: 'past',
          className: `wd-tseg-past${step.failed ? ' is-failed' : ''}`,
          style: { width: seg.past ? '100%' : '0%' },
        }),
        e('i', {
          key: 'fill',
          className: `wd-tseg-fill${step.done ? ' is-done' : ''}`,
          style: { width: `${Math.round(seg.pct * 100)}%` },
        }),
      ])
    )
  );

/**
 * The same steps twice: a labelled row above the content on a laptop, and a
 * narrow rail beside it on a phone, where the row has no width to spare.
 */
const ProgressBand = ({ isPhone }) => {
  const { goTo, bookingName } = useWeddingDay();
  const { steps, markerLeft, positionLabel } = useProgress();

  // The same header on both. A phone reads the steps the way a laptop does,
  // it just has less room for the words around them.
  return e('div', { className: `wd-toprail${isPhone ? ' is-phone' : ''}` }, [
    e('div', { className: 'wd-head', key: 'head' }, [
      e('span', { className: 'wd-eyebrow', key: 'eyebrow' }, 'WEDDING DAY DETAILS'),
      // Whose wedding this is, the way the creative preferences header
      // says it. A couple opens both forms off the same kind of link, so
      // the header has to answer which booking they are looking at.
      bookingName
        ? e('span', { className: 'wd-eyebrow-name', key: 'name' }, bookingName)
        : null,
      // Which step they are on. On a phone the countdown goes, since the
      // header has room for the step or for the deadline, and the card
      // they came from carries the date.
      e('span', { className: 'wd-status', key: 'status' }, [
        (positionLabel || '').toUpperCase(),
        isPhone ? null : e(Deadline, { key: 'deadline' }),
      ]),
    ]),
    e(
      'div',
      { className: 'wd-steps', key: 'steps' },
      [
        // Resolved against the row's real columns and gaps, so it lands
        // where the rails do.
        e('span', {
          className: 'wd-marker',
          key: 'marker',
          style: { left: markerLeft(isPhone) },
        }),
      ].concat(
      steps.map((step) =>
        e(
          'button',
          {
            key: step.key,
            type: 'button',
            className: `wd-step${step.current ? ' is-current' : ''}${step.dim ? ' is-dim' : ''}`,
            onClick: () => goTo(step.key),
          },
          [
            e('span', { className: 'wd-row', key: 'row' }, [
              e(Dot, { step, key: 'd' }),
              e('span', { className: 'wd-label', key: 'l' }, step.label),
            ]),
            e(Track, { step, key: 't' }),
          ]
        )
      ))
    ),
  ]);
};

export default ProgressBand;
